<template>
  <div
    id="app"
    class="vue-plugin-wrapper"
    :style="{
      '--main-color': mainColor,
      '--secondary-color': secondaryColor,
      '--background-color': backgroundColor,
      '--border-radius': borderRadius,
      '--contact-form-sticky-offset': contactFormStickyOffset
    }"
  >
    <div v-if="isSettingsLoading" class="twoqbsxml-initial-loader" aria-live="polite" aria-busy="true">
      <div class="twoqbsxml-initial-loader__box">
        <div class="twoqbsxml-initial-loader__spinner"></div>
        <div class="twoqbsxml-initial-loader__text">Ładowanie oferty...</div>
      </div>
    </div>

    <div v-show="!isSettingsLoading" class="d-block d-md-block d-lg-block">
      <div class="row mb-5 d-none d-md-flex d-lg-flex twoqbsAllFilters twoqbsDesktopGutter">
        <div class="twoqbsFiltersDesktop">
          <div class="twoqbsFiltersColumns">
            <div class="twoqbsFilterColumn">
              <div class="twoqbsFilterBlock">
                <label class="twoqbsFilterLabel">Typ nieruchomości:</label>
                <div class="twoqbsDropdownWrap">
                  <button
                    type="button"
                    class="twoqbsDropdownButton"
                    :disabled="hasSingleType"
                    :class="{ 'is-disabled': hasSingleType }"
                    @click="toggleFilterDropdown('type')"
                  >
                    <span>{{ getSelectedTypesLabel() }}</span>
                    <span v-if="!hasSingleType" class="twoqbsDropdownArrow" :class="{ 'is-open': showTypeFilters }" aria-hidden="true"></span>
                  </button>
                  <div v-show="showTypeFilters && !hasSingleType" class="twoqbsDropdownList text-start" @click.stop>
                    <div v-for="(item, index) in typeOf" :key="index" class="twoqbsCheckboxRow">
                      <input :id="'type-' + item" type="checkbox" v-model="checkedTypes" :value="item" />
                      <label :for="'type-' + item">{{ item }}</label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="twoqbsFilterBlock">
                <label class="twoqbsFilterLabel">Sortuj wg:</label>
                <div class="twoqbsDropdownWrap">
                  <button type="button" class="twoqbsDropdownButton" @click="toggleFilterDropdown('sort')">
                    <span v-html="getSelectedSortLabel()"></span>
                    <span class="twoqbsDropdownArrow" :class="{ 'is-open': showSortFilters }" aria-hidden="true"></span>
                  </button>
                  <div v-show="showSortFilters" class="twoqbsDropdownList text-start" @click.stop>
                    <div v-for="option in availableSortOptions" :key="option.value" class="twoqbsCheckboxRow">
                      <input
                        :id="'sort-' + option.value"
                        type="checkbox"
                        :checked="selectedSortOption === option.value"
                        @change="setSortOption(option.value); closeAllDropdowns();"
                      />
                      <label :for="'sort-' + option.value"><span v-html="option.label"></span></label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="twoqbsFilterColumn">
              <div class="twoqbsFilterBlock twoqbsFilterBlockWide">
                <label class="twoqbsFilterLabel">Cena:</label>
                <div class="twoqbsRangeInputs">
                  <input type="text" min="0" v-model="priceFromDisplay" @input="formatPriceInput('from', $event)" placeholder="od:" />
                  <input type="text" min="0" v-model="priceToDisplay" @input="formatPriceInput('to', $event)" placeholder="do:" />
                </div>
              </div>

              <div class="twoqbsFilterBlock twoqbsFilterBlockWide">
                <label class="twoqbsFilterLabel">Powierzchnia:</label>
                <div class="twoqbsRangeInputs">
                  <input type="number" min="0" step="1" v-model="areaFrom" placeholder="od:" />
                  <input type="number" min="0" step="1" v-model="areaTo" placeholder="do:" />
                </div>
              </div>
            </div>

            <div class="twoqbsFilterColumn">
              <div class="twoqbsFilterBlock">
                <label class="twoqbsFilterLabel">Liczba pokoi:</label>
                <div class="twoqbsDropdownWrap">
                  <button type="button" class="twoqbsDropdownButton" @click="toggleFilterDropdown('rooms')">
                    <span>{{ getSelectedRoomsLabel() }}</span>
                    <span class="twoqbsDropdownArrow" :class="{ 'is-open': showRoomsFilters }" aria-hidden="true"></span>
                  </button>
                  <div v-show="showRoomsFilters" class="twoqbsDropdownList text-start" @click.stop>
                    <div v-for="room in roomOptions" :key="room" class="twoqbsCheckboxRow">
                      <input :id="'room-' + room" type="checkbox" v-model="selectedRooms" :value="room" />
                      <label :for="'room-' + room">{{ room }} {{ getRoomLabel(room) }}</label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="twoqbsFilterBlock" v-if="hasResidentialFlats && floorOptions.length > 0">
                <label class="twoqbsFilterLabel">Piętro:</label>
                <div class="twoqbsDropdownWrap">
                  <button type="button" class="twoqbsDropdownButton" @click="toggleFilterDropdown('floor')">
                    <span>{{ getSelectedFloorLabel() }}</span>
                    <span class="twoqbsDropdownArrow" :class="{ 'is-open': showFloorFilters }" aria-hidden="true"></span>
                  </button>
                  <div v-show="showFloorFilters" class="twoqbsDropdownList text-start" @click.stop>
                    <div v-for="floor in floorOptions" :key="floor" class="twoqbsCheckboxRow">
                      <input :id="'floor-' + floor" type="checkbox" v-model="selectedFloors" :value="floor" />
                      <label :for="'floor-' + floor">{{ floor === 0 ? 'Parter' : 'Piętro ' + floor }}</label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="twoqbsFilterBlock twoqbsPerPageBlock">
                <label class="twoqbsFilterLabel">Na stronę:</label>
                <div class="twoqbsPerPagePills">
                  <button
                    v-for="option in perPageOptions"
                    :key="'per-page-' + option.value"
                    type="button"
                    class="twoqbsPerPagePill"
                    :class="{ 'is-active': isPerPageOptionSelected(option.value) }"
                    @click="setPerPageOption(option.value)"
                  >{{ option.label }}</button>
                </div>
              </div>
            </div>

            <div class="twoqbsFilterColumn">
              <div class="twoqbsFilterBlock">
                <label class="twoqbsFilterLabel">Dodatkowe informacje:</label>
                <div class="twoqbsDropdownWrap">
                  <button type="button" class="twoqbsDropdownButton" @click="toggleFilterDropdown('additional')">
                    <span>{{ getSelectedAdditionalLabel() }}</span>
                    <span class="twoqbsDropdownArrow" :class="{ 'is-open': showAdditionalFilters }" aria-hidden="true"></span>
                  </button>
                  <div v-show="showAdditionalFilters" class="twoqbsDropdownList text-start" @click.stop>
                    <div v-for="option in additionalOptions" :key="option.value" class="twoqbsCheckboxRow">
                      <input :id="'additional-' + option.value" type="checkbox" v-model="selectedAdditional" :value="option.value" />
                      <label :for="'additional-' + option.value">{{ option.label }}</label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="twoqbsFilterAction twoqbsFilterActionColumn">
                <label class="twoqbsFilterLabel twoqbsFilterLabelGhost" aria-hidden="true">Akcje:</label>
                <div class="twoqbsFilterButtonsRow">
                  <button type="button" class="twoqbsClearButton" @click="clearFilters">Wyczyść filtry</button>
                  <button type="button" class="twoqbsSearchButton" @click="applyFilters">Wyszukaj</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="twoqbsxml-layout d-none d-md-grid my-4 twoqbsDesktopGutter">
        <div class="twoqbsxml-results">
          <div class="twoqbsxml-grid">
            <div v-if="isFlatsLoading" class="twoqbsxml-results-loader">
              <div class="twoqbsxml-results-loader__spinner"></div>
              <p>Ładowanie lokali...</p>
            </div>
            <template v-else>
              <listing-card
                v-for="item in paginatedFlats"
                :key="item.id"
                :item="item"
                :is-mobile="false"
                :plan-href="getFlatUrl(item.local_number)"
                :flat-url="getFlatUrl(item.local_number)"
                :plugin-settings="pluginSettings"
                :investment-logo-url="investmentLogoUrl"
                :is-promotion-active="isPromotionActive"
                :has-mandatory-parking-spaces="hasMandatoryParkingSpaces"
                :get-mandatory-parking-spaces-value="getMandatoryParkingSpacesValue"
                :has-promotion-old-price="hasPromotionOldPrice"
                :get-display-price="getDisplayPrice"
                :get-display-price-mkw="getDisplayPriceMkw"
                :number-with-spaces="numberWithSpaces"
                :should-show-price-info="shouldShowPriceInfo"
                :load-price-history-info="loadPriceHistoryInfo"
                :get-price-info-tooltip-text="getPriceInfoTooltipText"
                :get-plan-image-src="getPlanImageSrc"
                @save-filters-state="saveFiltersState"
                @open-reservation-modal="showModal"
              />
            </template>
          <div v-if="!isFlatsLoading && sortedFlats.length === 0" class="twoqbsxml-no-results">
            <p style="color:red;">Brak lokali spełniających kryteria</p>
          </div>
        </div>

        <div v-if="!isFlatsLoading && sortedFlats.length > 0" class="twoqbsxml-pagination">
          <button
            v-for="(page, index) in pageNumbers"
            :key="'desktop-page-' + page + '-' + index"
            type="button"
            class="twoqbsxml-page-btn"
            :class="{ 'is-active': page === currentPage, 'is-ellipsis': page === '...' }"
            :disabled="page === '...'"
            @click="page !== '...' && goToPage(page)"
          >
            {{ page }}
          </button>
        </div>
      </div>

      <aside class="twoqbsxml-contact-sidebar">
        <h3>Zapraszam do kontaktu!</h3>

        <div v-if="listingAdvisor" class="twoqbsxml-advisor-info">
          <img
            v-if="listingAdvisor.photo"
            :src="listingAdvisor.photo"
            :alt="listingAdvisor.name || 'Doradca klienta'"
            class="twoqbsxml-advisor-photo"
          />
          <div class="twoqbsxml-advisor-details">
            <h4 v-if="listingAdvisor.name">{{ listingAdvisor.name }}</h4>
            <p v-if="listingAdvisor.position">{{ listingAdvisor.position }}</p>
            <a
              v-if="listingAdvisorPhone"
              :href="'tel:' + sanitizePhone(listingAdvisorPhone)"
              class="twoqbsxml-phone-desktop"
            >
              {{ listingAdvisorPhone }}
            </a>
          </div>
        </div>

        <a
          v-if="listingAdvisorPhone"
          :href="'tel:' + sanitizePhone(listingAdvisorPhone)"
          class="twoqbsxml-call-button twoqbsxml-call-mobile"
        >
          📞 Zadzwoń
        </a>

        <div v-if="listingFormError" class="twoqbsxml-form-error">{{ listingFormError }}</div>

        <form class="twoqbsxml-contact-form" @submit.prevent="submitListingContactForm">
          <input type="text" v-model="listingContactForm.contact_name" placeholder="Imię i nazwisko" required>
          <div>
            <input 
              type="email" 
              v-model="listingContactForm.contact_email" 
              placeholder="E-mail" 
              @blur="validateEmail"
              @input="listingEmailError = ''"
              :class="{ 'twoqbsxml-input-error': listingEmailError }"
              required
            >
            <div v-if="listingEmailError" class="twoqbsxml-field-error">{{ listingEmailError }}</div>
          </div>
          <div>
            <input 
              type="tel" 
              v-model="listingContactForm.contact_phone" 
              placeholder="Telefon"
              @blur="validatePhone"
              @input="listingPhoneError = ''"
              :class="{ 'twoqbsxml-input-error': listingPhoneError }"
            >
            <div v-if="listingPhoneError" class="twoqbsxml-field-error">{{ listingPhoneError }}</div>
          </div>
          <textarea v-model="listingContactForm.contact_message" placeholder="Treść zapytania" required></textarea>

          <label class="twoqbsxml-consent-row">
            <input type="checkbox" v-model="listingConsent1" required>
            <span class="twoqbsxml-consent-text">
              <span v-html="listingConsentText1PreviewHtml"></span>
              <template v-if="hasListingConsentReadMore">
                <a
                  v-if="!listingConsentExpanded"
                  href="#"
                  class="twoqbsxml-read-more-link"
                  @click.prevent="listingConsentExpanded = !listingConsentExpanded"
                >
                  czytaj więcej
                </a>
                <template v-else>
                  <span class="twoqbsxml-consent-more-inline" v-html="listingConsentText1MoreHtml"></span>
                  <a
                    href="#"
                    class="twoqbsxml-read-more-link"
                    @click.prevent="listingConsentExpanded = false"
                  >
                    zwiń
                  </a>
                </template>
              </template>
            </span>
          </label>

          <label class="twoqbsxml-consent-row twoqbsxml-consent-title">
            {{ listingConsentLegalIntro }}
          </label>

          <label class="twoqbsxml-consent-row">
            <input type="checkbox" v-model="listingConsent2" required>
            {{ listingConsentText2 }}
          </label>

          <label class="twoqbsxml-consent-row">
            <input type="checkbox" v-model="listingConsent3" required>
            {{ listingConsentText3 }}
          </label>

          <div v-if="listingFormSuccess" class="twoqbsxml-form-success twoqbsxml-form-success-inline">Dziękujemy! Twoja wiadomość została wysłana.</div>

          <button type="submit" class="twoqbsxml-submit-button" :disabled="listingFormSending">
            {{ listingFormSending ? 'Wysyłanie...' : 'Wyślij zapytanie' }}
          </button>
        </form>
      </aside>
    </div>
    </div>

    <div class="d-block d-sm-block d-md-none my-3">
      <div class="d-block d-md-none mx-auto">
        <button class="twoqbsxml-mobile-filters-btn" :disabled="isFlatsLoading" @click="showMobileFilters = true">
          <span v-if="isFlatsLoading">Ładowanie lokali...</span>
          <span v-else-if="sortedFlats.length === 0" style="color:red;">Brak lokali spełniających kryteria</span>
          <span v-else>Pokaż filtry </span>
        </button>
        <div v-if="showMobileFilters" class="mobile-filters-overlay">
          <div class="mobile-filters-panel">
            <button class="btn-close" @click="showMobileFilters = false">×</button>
            <div class="mb-3">
              <b>Typ nieruchomości</b>
              <div class="twoqbsTypeCheckboxList text-start text-nowrap">
                <div v-for="(item, index) in typeOf" :key="index">
                  <input :id="'mobile-' + item" type="checkbox" v-model="checkedTypes" :value="item" :disabled="hasSingleType" />
                  <label :for="'mobile-' + item">{{ item }}</label>
                </div>
              </div>
            </div>

            <div class="mb-3 text-start">
              <b>Cena</b>
              <div class="twoqbsRangeInputs mt-2">
                <input type="text" min="0" v-model="priceFromDisplay" @input="formatPriceInput('from', $event)" placeholder="od:" />
                <input type="text" min="0" v-model="priceToDisplay" @input="formatPriceInput('to', $event)" placeholder="do:" />
              </div>
            </div>

            <div class="mb-3 text-start">
              <b>Powierzchnia</b>
              <div class="twoqbsRangeInputs mt-2">
                <input type="number" min="0" step="1" v-model="areaFrom" placeholder="od:" />
                <input type="number" min="0" step="1" v-model="areaTo" placeholder="do:" />
              </div>
            </div>

            <div class="mb-3 text-start">
              <b>Liczba pokoi</b>
              <div class="twoqbsTypeCheckboxList text-start text-nowrap mt-2">
                <div v-for="room in roomOptions" :key="'mobile-room-' + room">
                  <input :id="'mobile-room-' + room" type="checkbox" v-model="selectedRooms" :value="room" />
                  <label :for="'mobile-room-' + room">{{ room }} {{ getRoomLabel(room) }}</label>
                </div>
              </div>
            </div>

              <div class="mb-3 text-start" v-if="hasResidentialFlats && floorOptions.length > 0">
              <b>Piętro</b>
              <div class="twoqbsTypeCheckboxList text-start text-nowrap mt-2">
                <div v-for="floor in floorOptions" :key="'mobile-floor-' + floor">
                  <input :id="'mobile-floor-' + floor" type="checkbox" v-model="selectedFloors" :value="floor" />
                  <label :for="'mobile-floor-' + floor">{{ floor === 0 ? 'Parter' : 'Piętro ' + floor }}</label>
                </div>
              </div>
            </div>

            <div class="mb-3 text-start">
              <b>Dodatkowe informacje</b>
              <div class="twoqbsTypeCheckboxList text-start text-nowrap mt-2">
                <div v-for="option in additionalOptions" :key="'mobile-additional-' + option.value">
                  <input :id="'mobile-additional-' + option.value" type="checkbox" v-model="selectedAdditional" :value="option.value" />
                  <label :for="'mobile-additional-' + option.value">{{ option.label }}</label>
                </div>
              </div>
            </div>

            <div class="mb-3 text-start">
              <b>Sortuj wg</b>
              <div class="twoqbsTypeCheckboxList text-start text-nowrap mt-2">
                <div v-for="option in availableSortOptions" :key="'mobile-sort-' + option.value">
                  <input
                    :id="'mobile-sort-' + option.value"
                    type="checkbox"
                    :checked="selectedSortOption === option.value"
                    @change="setSortOption(option.value)"
                  />
                  <label :for="'mobile-sort-' + option.value"><span v-html="option.label"></span></label>
                </div>
              </div>
            </div>

            <div class="mb-3 text-start">
              <b>Na stronę</b>
              <div class="twoqbsPerPagePills mt-2">
                <button
                  v-for="option in perPageOptions"
                  :key="'mobile-per-page-' + option.value"
                  type="button"
                  class="twoqbsPerPagePill"
                  :class="{ 'is-active': isPerPageOptionSelected(option.value) }"
                  @click="setPerPageOption(option.value)"
                >{{ option.label }}</button>
              </div>
            </div>

            <button class="btn btn-outline-dark w-100" :disabled="isFlatsLoading" @click="applyFilters(); showMobileFilters = false">
              <span v-if="isFlatsLoading">Ładowanie lokali...</span>
              <span v-else-if="sortedFlats.length === 0" style="color:red;">Brak lokali spełniających kryteria</span>
              <span v-else>Wyszukaj ({{ sortedFlats.length }}) </span>
            </button>
            <button class="btn btn-outline-secondary w-100 mt-2" @click="clearFilters(); showMobileFilters = false">Wyczyść filtry</button>
          </div>
        </div>
      </div>

      <div class="twoqbsxml-grid d-block d-md-none mx-auto px-2">
        <div v-if="isFlatsLoading" class="twoqbsxml-results-loader">
          <div class="twoqbsxml-results-loader__spinner"></div>
          <p>Ładowanie lokali...</p>
        </div>
        <template v-else>
          <listing-card
            v-for="item in paginatedFlats"
            :key="item.id"
            :item="item"
            :is-mobile="true"
            :plan-href="getPlanHref(item)"
            :flat-url="getFlatUrl(item.local_number)"
            :plugin-settings="pluginSettings"
            :investment-logo-url="investmentLogoUrl"
            :is-promotion-active="isPromotionActive"
            :has-mandatory-parking-spaces="hasMandatoryParkingSpaces"
            :get-mandatory-parking-spaces-value="getMandatoryParkingSpacesValue"
            :has-promotion-old-price="hasPromotionOldPrice"
            :get-display-price="getDisplayPrice"
            :get-display-price-mkw="getDisplayPriceMkw"
            :number-with-spaces="numberWithSpaces"
            :should-show-price-info="shouldShowPriceInfo"
            :load-price-history-info="loadPriceHistoryInfo"
            :get-price-info-tooltip-text="getPriceInfoTooltipText"
            :get-plan-image-src="getPlanImageSrc"
            @save-filters-state="saveFiltersState"
            @open-reservation-modal="showModal"
          />
        </template>

        <div v-if="!isFlatsLoading && sortedFlats.length === 0" class="twoqbsxml-no-results">
          <p style="color:red;">Brak lokali spełniających kryteria</p>
        </div>
      </div>

      <div v-if="!isFlatsLoading && sortedFlats.length > 0" class="twoqbsxml-pagination d-md-none px-2">
        <button
          v-for="(page, index) in pageNumbers"
          :key="'mobile-page-' + page + '-' + index"
          type="button"
          class="twoqbsxml-page-btn"
          :class="{ 'is-active': page === currentPage, 'is-ellipsis': page === '...' }"
          :disabled="page === '...'"
          @click="page !== '...' && goToPage(page)"
        >
          {{ page }}
        </button>
      </div>
    </div>

    <contact-form
      v-show="!isSettingsLoading"
      :flat="flat"
      :advisor="listingAdvisor"
      :advisor-phone="listingAdvisorPhone"
    ></contact-form>
  </div>
</template>

<script>
import axios from "axios";
import ContactForm from "./components/ContactForm";
import ListingCard from "./components/ListingCard";

export default {
  name: "App",
  data() {
    return {
      url: window.location.origin,
      // url: "https://osiedleroztocze.pl",-> do testów lokalnych
      dataUrl: this.url + "/wp-json/twoqbs/v1/flats?nocache=1",
      singleFlatUrl: this.url + "/wp-json/twoqbs/v1/flat?nocache=1",
      tableFlats: [],
      flats: [],
      investmentInfo: [],
      pluginSettings: [],
      isSettingsLoading: true,
      isFlatsLoading: true,
      mainColor: "#63b0bd",
      secondaryColor: "#2c3e50",
      backgroundColor: "#f5f5f5",
      borderRadius: "16px",
      contactFormStickyOffset: "20px",
      //--Filtrowanie.
      minPrice: 0,
      maxPrice: 0,
      priceFrom: "",
      priceTo: "",
      priceFromDisplay: "",
      priceToDisplay: "",
      minArea: 0,
      maxArea: 0,
      areaFrom: "",
      areaTo: "",
      areaAutoSort: false,
      priceAutoSort: false,
      minRooms: 0,
      maxRooms: 0,
      roomOptions: [],
      flat: {},
      showTypeFilters: false,
      showRoomsFilters: false,
      showFloorFilters: false,
      showAdditionalFilters: false,
      showSortFilters: false,
      showPerPageFilters: false,
      checkedTypes: [],
      selectedRooms: [],
      selectedFloors: [],
      selectedAdditional: [],
      selectedSortOption: "",
      itemsPerPageMode: "auto",
      manualItemsPerPage: null,
      perPageOptions: [
        { value: 'auto', label: 'Auto' },
        { value: 6, label: '6' },
        { value: 12, label: '12' },
        { value: 24, label: '24' },
        { value: 48, label: '48' }
      ],
      additionalOptions: [
        { value: "ogrodek", label: "Ogródek" },
        { value: "balkon", label: "Balkon" },
        { value: "loggia", label: "Loggia" },
        { value: "taras", label: "Taras" },
      ],
      sortOptions: [
        { value: "promotion", label: "W promocji" },
        { value: "price_asc", label: "Cena: od najniższej" },
        { value: "price_desc", label: "Cena: od najwyższej" },
        { value: "price_mkw_asc", label: "Cena za m<sup>2</sup>: od najniższej" },
        { value: "price_mkw_desc", label: "Cena za m<sup>2</sup>: od najwyższej" },
        { value: "area_asc", label: "Powierzchnia: od najniższej" },
        { value: "area_desc", label: "Powierzchnia: od najwyższej" },
        { value: "floor_asc", label: "Piętro: od najniższego" },
        { value: "floor_desc", label: "Piętro: od najwyższego" },
      ],
      typeOf: [],
      today: new Date().toISOString().split('T')[0],
      expandedRows: [],  // rozwijana wiersz
      selectedFlats: [], // tu będą ID zaznaczonych mieszkań
      showMobileFilters: false,
      dynamicSearchTimer: null,
      dynamicSearchDelay: 250,
      itemsPerPage: 10,
      currentPage: 1,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1920,
      outsideClickHandler: null,
      resizeHandler: null,
      pageShowHandler: null,
      listingAdvisor: null,
      listingContactForm: {
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        contact_message: "Proszę o kontakt w sprawie oferty.",
        contact_flat: "Lista ofert"
      },
      listingConsent1: false,
      listingConsent2: false,
      listingConsent3: false,
      listingConsentExpanded: false,
      listingConsentText1Default: 'Zapoznałem się z <a href="/polityka-prywatnosci" target="_blank">Polityką prywatności</a> i wyrażam zgodę na przetwarzanie moich danych osobowych przez Develop Me Sp. z o.o. z siedzibą we Wrocławiu, adres: ul. Zdrowa 24/6A , 53-511 Wrocław, wpisana do Rejestru Przedsiębiorców Krajowego Rejestru Sądowego prowadzonego przez Sąd Rejonowy dla Wrocławia Fabrycznej we Wrocławiu, VI Wydział Gospodarczy Krajowego Rejestru Sądowego, pod nr KRS 0000980166, NIP 8971908261 w celach marketingowych.',
      listingConsentLegalIntroDefault: 'Wyrażam zgodę w rozumieniu art. 398 ust. 1 pkt 2 Ustawy z dnia 12 lipca 2024 r. – Prawo komunikacji elektronicznej (Dz.U.2024.0.1221) na:',
      listingConsentText2Default: 'wysyłanie informacji handlowych drogą elektroniczną (email, newsletter)',
      listingConsentText3Default: 'prowadzenie marketingu bezpośredniego za pośrednictwem połączeń telefonicznych',
      listingFormSending: false,
      listingFormSuccess: false,
      listingFormError: "",
      listingEmailError: "",
      listingPhoneError: "",
      priceInfoCache: {}

    };
  },
  components: {
    "contact-form": ContactForm,
    ListingCard
  },
  methods: {

    toggleRow(id) {
      if (this.expandedRows.includes(id)) {
        this.expandedRows = this.expandedRows.filter(rowId => rowId !== id);
      } else {
        this.expandedRows.push(id);
      }
    },

    formatPriceInput(type, event) {
      // Pobierz wartość z inputu
      let value = event.target.value;
      
      // Usuń wszystkie spacje (stare formatowanie)
      let cleanValue = value.replace(/\s/g, '');
      
      // Parsuj na liczbę
      const numValue = parseInt(cleanValue) || '';
      
      // Formatuj z odstępami co 3 cyfry (od prawa)
      let formatted = '';
      if (numValue !== '') {
        formatted = numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      }
      
      // Ustaw wyświetlaną wartość
      if (type === 'from') {
        this.priceFromDisplay = formatted;
        this.priceFrom = numValue;
      } else if (type === 'to') {
        this.priceToDisplay = formatted;
        this.priceTo = numValue;
      }
    },

    isPromotionActive(item) {
      console.log('Promocja:', item.promotion, 'DateFrom:', item.promotion_date_from, 'DateTo:', item.promotion_date_to);
      
      // Warunek 1: Flaga promocji musi być włączona
      if (item.promotion != 1) {
        console.log('Promocja wyłączona');
        return false;
      }
      
      // Warunek 2: Muszą istnieć ceny promocyjne (większe od 0)
      const hasPromotionPrice = item.promotion_price && Number(item.promotion_price) > 0;
      const hasPromotionPriceMkw = item.promotion_price_mkw && Number(item.promotion_price_mkw) > 0;
      
      if (!hasPromotionPrice || !hasPromotionPriceMkw) {
        console.log('Brak cen promocyjnych - promocja nieaktywna');
        return false;
      }
      
      const today = this.today;
      const dateFrom = item.promotion_date_from;
      const dateTo = item.promotion_date_to;
      
      // Pomocnicza funkcja - sprawdza czy wartość jest pusta
      const isEmpty = (val) => {
        return !val || val === '' || (Array.isArray(val) && val.length === 0);
      };
      
      // Scenariusz 1: Promocja bez dat - zawsze aktywna
      if (isEmpty(dateFrom) && isEmpty(dateTo)) {
        console.log('SCENARIUSZ 1: Promocja bezterminowa - AKTYWNA');
        return true;
      }
      
      // Scenariusz 2: Tylko data "do" (bez daty "od") - aktywna do daty "do"
      if (isEmpty(dateFrom) && !isEmpty(dateTo)) {
        return today <= dateTo;
      }
      
      // Scenariusz 3: Tylko data "od" (bez daty "do") - aktywna od daty "od" w nieskończoność
      if (!isEmpty(dateFrom) && isEmpty(dateTo)) {
        return dateFrom <= today;
      }
      
      // Scenariusz 4: Obie daty ustawione - klasyczny przedział
      if (!isEmpty(dateFrom) && !isEmpty(dateTo)) {
        return dateFrom <= today && today <= dateTo;
      }
      
      // Fallback - jeśli coś poszło nie tak
      return false;
    },

    numberWithSpaces(price, forceTwoDecimals = false) {
      if (price === null || price === undefined || price === "") return "";
      const num = Number(price);
      if (isNaN(num)) return price;
      if (!forceTwoDecimals && Number.isInteger(num)) {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
      }

      const parts = num.toFixed(2).split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");
      return parts.join(",");
    },

    getDisplayPrice(item) {
      if (item.promotion == 1 && this.isPromotionActive(item) && Number(item.promotion_price) > 0) {
        return item.promotion_price;
      }

      if (Number(item.price) > 0) {
        return item.price;
      }

      return null;
    },

    hasPromotionOldPrice(item) {
      return item.promotion == 1
        && this.isPromotionActive(item)
        && Number(item.promotion_price) > 0
        && Number(item.price) > 0;
    },

    getDisplayPriceMkw(item) {
      if (item.promotion == 1 && this.isPromotionActive(item) && Number(item.promotion_price_mkw) > 0) {
        return item.promotion_price_mkw;
      }

      if (Number(item.pricemkw) > 0) {
        return item.pricemkw;
      }

      return null;
    },

    shouldShowPriceInfo(item) {
      return !this.isResidentialType(item) && this.getDisplayPrice(item) !== null;
    },

    isResidentialType(item) {
      const type = String(item?.type || '').trim().toLowerCase();
      return type === 'mieszkanie' || type === 'dom';
    },

    toTimestamp(rawDate) {
      if (!rawDate) return null;
      const parsed = new Date(rawDate);
      if (!Number.isNaN(parsed.getTime())) return parsed.getTime();

      const dash = String(rawDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dash) return new Date(`${dash[1]}-${dash[2]}-${dash[3]}T00:00:00`).getTime();

      const dots = String(rawDate).match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
      if (dots) return new Date(`${dots[3]}-${dots[2].padStart(2, '0')}-${dots[1].padStart(2, '0')}T00:00:00`).getTime();

      return null;
    },

    formatTooltipDate(rawDate) {
      if (!rawDate) return '';
      const date = new Date(rawDate);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('pl-PL');
      }

      const match = String(rawDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        return `${match[3]}.${match[2]}.${match[1]}`;
      }

      return String(rawDate);
    },

    getLowestPriceLast30Days(history) {
      const now = Date.now();
      const days30 = 30 * 24 * 60 * 60 * 1000;

      const inRange = history
        .filter((entry) => typeof entry.price === 'number')
        .filter((entry) => {
          const timestamp = this.toTimestamp(entry.date);
          return timestamp && now - timestamp >= 0 && now - timestamp <= days30;
        })
        .map((entry) => entry.price);

      if (!inRange.length) {
        return null;
      }

      return Math.min(...inRange);
    },

    async loadPriceHistoryInfo(item) {
      const itemId = item?.id;
      if (!itemId) {
        return;
      }

      const cacheKey = String(itemId);
      const existing = this.priceInfoCache[cacheKey];
      if (existing && (existing.loading || existing.loaded)) {
        return;
      }

      this.$set(this.priceInfoCache, cacheKey, {
        loading: true,
        loaded: false,
        error: '',
        lowest30: null,
        history: []
      });

      try {
        const response = await axios.get(`${window.location.origin}/wp-json/twoqbs/v1/flat/${itemId}?nocache=${Date.now()}`);
        const rawChanges = response?.data?.price_change_history?.realestate?.price_history?.price_change;
        const list = Array.isArray(rawChanges) ? rawChanges : (rawChanges ? [rawChanges] : []);

        const normalized = list
          .map((entry) => {
            const priceRaw = entry?.after?.price ?? entry?.after_price ?? entry?.price ?? null;
            const priceValue = Number(priceRaw);
            return {
              date: entry?.date_modified ?? entry?.date ?? null,
              price: Number.isFinite(priceValue) ? priceValue : null
            };
          })
          .filter((entry) => entry.date && entry.price !== null)
          .sort((a, b) => (this.toTimestamp(b.date) || 0) - (this.toTimestamp(a.date) || 0));

        this.$set(this.priceInfoCache, cacheKey, {
          loading: false,
          loaded: true,
          error: '',
          lowest30: this.getLowestPriceLast30Days(normalized),
          history: normalized
        });
      } catch (error) {
        this.$set(this.priceInfoCache, cacheKey, {
          loading: false,
          loaded: true,
          error: 'Nie udało się pobrać danych o cenie.',
          lowest30: null,
          history: []
        });
      }
    },

    getPriceInfoTooltipText(item) {
      const itemId = item?.id;
      if (!itemId) {
        return 'Brak identyfikatora lokalu.';
      }

      const state = this.priceInfoCache[String(itemId)];
      if (!state) {
        return 'Najedź, aby wczytać informacje o najniższej cenie i historii cen.';
      }

      if (state.loading) {
        return 'Ładowanie danych o cenie...';
      }

      if (state.error) {
        return state.error;
      }

      const lines = [];

      if (state.lowest30 === null) {
        lines.push('Najniższa cena z ostatnich 30 dni: brak danych.');
      } else {
        lines.push(`Najniższa cena z ostatnich 30 dni: ${this.numberWithSpaces(state.lowest30, true)} zł`);
      }

      if (!state.history.length) {
        lines.push('Historia cen: Brak zmiany ceny.');
        return lines.join('\n');
      }

      lines.push('Historia cen:');
      state.history.slice(0, 3).forEach((entry) => {
        lines.push(`${this.formatTooltipDate(entry.date)} - ${this.numberWithSpaces(entry.price, true)} zł`);
      });

      if (state.history.length > 3) {
        lines.push('...');
      }

      return lines.join('\n');
    },

    getSelectedTypesLabel() {
      if (this.hasSingleType) {
        return this.typeOf[0] || "Wybierz";
      }
      if (!this.checkedTypes.length) {
        return "Wybierz";
      }
      if (this.checkedTypes.length === 1) {
        return this.checkedTypes[0];
      }
      return `${this.checkedTypes[0]} (+${this.checkedTypes.length - 1})`;
    },

    getSelectedRoomsLabel() {
      if (!this.selectedRooms.length) {
        return "Wybierz";
      }
      if (this.selectedRooms.length === 1) {
        const room = this.selectedRooms[0];
        return `${room} ${this.getRoomLabel(room)}`;
      }
      return `${this.selectedRooms.length} wybrane`;
    },

    getSelectedAdditionalLabel() {
      if (!this.selectedAdditional.length) {
        return "Wybierz";
      }
      if (this.selectedAdditional.length === 1) {
        const found = this.additionalOptions.find((option) => option.value === this.selectedAdditional[0]);
        return found ? found.label : "Wybierz";
      }
      return `${this.selectedAdditional.length} wybrane`;
    },

    getSelectedSortLabel() {
      if (!this.selectedSortOption) {
        return "Wybierz";
      }
      const found = this.sortOptions.find((option) => option.value === this.selectedSortOption);
      return found ? found.label : "Wybierz";
    },

    getSelectedPerPageLabel() {
      if (this.itemsPerPageMode === 'auto') {
        return `Auto (${this.itemsPerPage})`;
      }

      return String(this.manualItemsPerPage || this.itemsPerPage);
    },

    isPerPageOptionSelected(value) {
      if (value === 'auto') {
        return this.itemsPerPageMode === 'auto';
      }

      return this.itemsPerPageMode === 'manual' && Number(value) === Number(this.manualItemsPerPage);
    },

    setPerPageOption(value) {
      if (value === 'auto') {
        this.itemsPerPageMode = 'auto';
        this.manualItemsPerPage = null;
      } else {
        const parsed = Number(value);
        if (!Number.isFinite(parsed) || parsed <= 0) {
          return;
        }

        this.itemsPerPageMode = 'manual';
        this.manualItemsPerPage = parsed;
      }

      this.savePerPagePreference();
      this.updateItemsPerPageForViewport();
    },

    savePerPagePreference() {
      if (typeof window === 'undefined') {
        return;
      }

      const payload = {
        mode: this.itemsPerPageMode,
        value: this.manualItemsPerPage
      };
      localStorage.setItem('twoqbs_items_per_page_pref', JSON.stringify(payload));
    },

    loadPerPagePreference() {
      if (typeof window === 'undefined') {
        return;
      }

      try {
        const raw = localStorage.getItem('twoqbs_items_per_page_pref');
        if (!raw) {
          return;
        }

        const payload = JSON.parse(raw);
        if (payload?.mode === 'manual') {
          const parsed = Number(payload?.value);
          if (Number.isFinite(parsed) && parsed > 0) {
            this.itemsPerPageMode = 'manual';
            this.manualItemsPerPage = parsed;
            return;
          }
        }

        this.itemsPerPageMode = 'auto';
        this.manualItemsPerPage = null;
      } catch (error) {
        console.error('Błąd odczytu ustawień liczby ofert na stronie:', error);
        this.itemsPerPageMode = 'auto';
        this.manualItemsPerPage = null;
      }
    },

    getRoomLabel(room) {
      const number = Number(room);
      if (number === 1) return "pokój";
      if (number >= 2 && number <= 4) return "pokoje";
      return "pokoi";
    },

    setSortOption(option) {
      this.selectedSortOption = this.selectedSortOption === option ? "" : option;
    },

    toggleFilterDropdown(type) {
      if (type === "type" && this.hasSingleType) {
        return;
      }

      const wasTypeOpen = this.showTypeFilters;
      const wasRoomsOpen = this.showRoomsFilters;
      const wasFloorOpen = this.showFloorFilters;
      const wasAdditionalOpen = this.showAdditionalFilters;
      const wasSortOpen = this.showSortFilters;
      const wasPerPageOpen = this.showPerPageFilters;

      this.showTypeFilters = false;
      this.showRoomsFilters = false;
      this.showFloorFilters = false;
      this.showAdditionalFilters = false;
      this.showSortFilters = false;
      this.showPerPageFilters = false;

      if (type === "type") {
        this.showTypeFilters = !wasTypeOpen;
      }
      if (type === "rooms") {
        this.showRoomsFilters = !wasRoomsOpen;
      }
      if (type === "floor") {
        this.showFloorFilters = !wasFloorOpen;
      }
      if (type === "additional") {
        this.showAdditionalFilters = !wasAdditionalOpen;
      }
      if (type === "sort") {
        this.showSortFilters = !wasSortOpen;
      }
      if (type === "perPage") {
        this.showPerPageFilters = !wasPerPageOpen;
      }
    },

    closeAllDropdowns() {
      this.showTypeFilters = false;
      this.showRoomsFilters = false;
      this.showFloorFilters = false;
      this.showAdditionalFilters = false;
      this.showSortFilters = false;
      this.showPerPageFilters = false;
    },

    handleOutsideClick(event) {
      // Sprawdzaj czy kliknięcie było na którymś z dropdownów
      const dropdownWrap = event.target.closest('.twoqbsDropdownWrap');
      if (dropdownWrap) {
        return; // Kliknięcie było na przycisku lub liście dropdownu
      }
      // Zamknij dropdowny jeśli kliknięcie było poza dropdownami
      this.closeAllDropdowns();
    },

    parseFilterNumber(value) {
      if (value === "" || value === null || value === undefined) {
        return null;
      }

      const parsed = Number(String(value).replace(',', '.'));
      return Number.isFinite(parsed) ? parsed : null;
    },

    getItemArea(item) {
      if (item.area && Number(item.area) > 0) {
        return Number(item.area);
      }
      if (item.area_usable && Number(item.area_usable) > 0) {
        return Number(item.area_usable);
      }
      return 0;
    },

    compareByArea(a, b, direction = 'asc') {
      if (direction === 'asc') {
        const isResidentialA = this.isResidentialType(a);
        const isResidentialB = this.isResidentialType(b);

        // Przy sortowaniu rosnącym najpierw pokazuj mieszkania i domy,
        // a dopiero potem pozostałe typy lokali.
        if (isResidentialA && !isResidentialB) return -1;
        if (!isResidentialA && isResidentialB) return 1;
      }

      const areaA = this.getItemArea(a);
      const areaB = this.getItemArea(b);
      const hasAreaA = areaA > 0;
      const hasAreaB = areaB > 0;

      // Brak metrazu zawsze na koncu listy.
      if (!hasAreaA && hasAreaB) return 1;
      if (hasAreaA && !hasAreaB) return -1;
      if (!hasAreaA && !hasAreaB) return 0;

      return direction === 'desc' ? areaB - areaA : areaA - areaB;
    },

    getPlanLinkParts(item) {
      const planLink = item?.plan_link;

      if (Array.isArray(planLink)) {
        const values = planLink
          .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
          .filter((entry) => entry !== '');
        return values;
      }

      if (typeof planLink === 'string' && planLink.trim() !== '') {
        return [planLink.trim()];
      }

      return [];
    },

    getPlanImageSrc(item) {
      const parts = this.getPlanLinkParts(item);
      if (!parts.length) {
        return '';
      }

      return parts[0] || '';
    },

    getPlanHref(item) {
      const parts = this.getPlanLinkParts(item);
      if (!parts.length) {
        return '#';
      }

      return parts[1] || parts[0] || '#';
    },

    itemHasAdditionalFeature(item, feature) {
      if (feature === "ogrodek") {
        return !!(item.ogrod && String(item.ogrod).trim() !== '' && item.ogrod !== '0');
      }
      if (feature === "balkon") {
        const value = item.balkon ?? item.balcony;
        return !!(value && String(value).trim() !== '' && value !== '0');
      }
      if (feature === "loggia") {
        return !!(item.loggia && String(item.loggia).trim() !== '' && item.loggia !== '0');
      }
      if (feature === "taras") {
        const value = item.taras ?? item.terrace;
        return !!(value && String(value).trim() !== '' && value !== '0');
      }
      return false;
    },

    getMandatoryParkingSpacesValue(item) {
      const value = item.number_of_mandatory_parking_spaces;

      if (Array.isArray(value)) {
        const firstNonEmpty = value.find((entry) => String(entry).trim() !== '' && String(entry) !== '0');
        return firstNonEmpty ?? '';
      }

      if (value === null || value === undefined) {
        return '';
      }

      return value;
    },

    hasMandatoryParkingSpaces(item) {
      const value = this.getMandatoryParkingSpacesValue(item);
      if (value === '') {
        return false;
      }

      return String(value).trim() !== '' && String(value) !== '0';
    },

    isSoldStatus(item) {
      const soldStatusNames = [
        "Sprzedany",
        "Umowa końcowa",
        "Umowa deweloperska",
        "Umowa przedwstępna"
      ];

      if (soldStatusNames.includes(item.status_name)) {
        return true;
      }

      return Number(item.status_id) === 9;
    },

    compare(a, b) {
      // Jeśli jest wybrана opcja sortowania, używaj jej
      if (this.selectedSortOption) {
        switch (this.selectedSortOption) {
          case 'price_asc': {
            const priceA = Number(this.isPromotionActive(a) ? a.promotion_price : a.price) || 0;
            const priceB = Number(this.isPromotionActive(b) ? b.promotion_price : b.price) || 0;
            return priceA - priceB;
          }
          case 'price_desc': {
            const priceA2 = Number(this.isPromotionActive(a) ? a.promotion_price : a.price) || 0;
            const priceB2 = Number(this.isPromotionActive(b) ? b.promotion_price : b.price) || 0;
            return priceB2 - priceA2;
          }
          case 'area_asc': {
            return this.compareByArea(a, b, 'asc');
          }
          case 'area_desc': {
            return this.compareByArea(a, b, 'desc');
          }
          default:
            break;
        }
      }
      
      // Domyślne sortowanie (brak wybranej opcji): Mieszkania na początku, potem po ID
      const isAFlat = a.type === "Mieszkanie";
      const isBFlat = b.type === "Mieszkanie";
      
      if (isAFlat && !isBFlat) return -1;  // a jest mieszkaniem, b nie - a pierwsze
      if (!isAFlat && isBFlat) return 1;   // b jest mieszkaniem, a nie - b pierwsze
      
      // Jeśli oba są tego samego typu (lub obie nie-mieszkania), sortuj po id
      if (a.id < b.id) return -1;
      if (a.id > b.id) return 1;
      
      return 0;
    },

    scheduleDynamicSearch() {
      if (this.dynamicSearchTimer) {
        clearTimeout(this.dynamicSearchTimer);
      }

      this.dynamicSearchTimer = setTimeout(() => {
        this.applyFilters(false);
      }, this.dynamicSearchDelay);
    },

    applyFilters(closeDropdowns = true) {
      if (!this.flats?.realestate) {
        this.tableFlats = [];
        this.currentPage = 1;
        return;
      }

      if (closeDropdowns) {
        this.closeAllDropdowns();
      }

      const minPriceInput = this.parseFilterNumber(this.priceFrom);
      const maxPriceInput = this.parseFilterNumber(this.priceTo);
      const minAreaInput = this.parseFilterNumber(this.areaFrom);
      const maxAreaInput = this.parseFilterNumber(this.areaTo);

      this.tableFlats = [];
      this.flats.realestate.forEach((item) => {
        const itemPrice = Number(this.isPromotionActive(item)
          ? item.promotion_price
          : item.price);

        const itemArea = this.getItemArea(item);
        const itemRooms = Number(item.rooms || 0);
        const itemFloor = item.floor !== null && item.floor !== undefined && item.floor !== '' ? Number(item.floor) : null;

        const inStage = this.checkedTypes.length !== 0 ? this.checkedTypes.includes(item.type) : true;
        const inPriceFrom = minPriceInput === null ? true : itemPrice >= minPriceInput;
        const inPriceTo = maxPriceInput === null ? true : itemPrice <= maxPriceInput;
        const inAreaFrom = minAreaInput === null ? true : itemArea >= minAreaInput;
        const inAreaTo = maxAreaInput === null ? true : itemArea <= maxAreaInput;
        const inRooms = this.selectedRooms.length ? this.selectedRooms.includes(itemRooms) : true;
        const inFloor = this.selectedFloors.length ? (itemFloor !== null && this.selectedFloors.includes(itemFloor)) : true;
        const inAdditional = this.selectedAdditional.length
          ? this.selectedAdditional.every((feature) => this.itemHasAdditionalFeature(item, feature))
          : true;
        const hasMainPrice = this.getDisplayPrice(item) !== null;

        if (inStage && inPriceFrom && inPriceTo && inAreaFrom && inAreaTo && inRooms && inFloor && inAdditional && hasMainPrice) {
          if (item.dont_send_to_www == 0 && !this.isSoldStatus(item)) {
            this.tableFlats.push(item);
          }
        }
      });

      this.tableFlats.sort(this.compare);
      this.updateVisibleStages();
      this.currentPage = 1;
    },

    applyAreaAutoSort(areaFrom, areaTo) {
      const hasArea = (areaFrom !== '' && areaFrom !== null && areaFrom !== undefined) ||
                      (areaTo !== '' && areaTo !== null && areaTo !== undefined);
      if (hasArea && !this.selectedSortOption) {
        this.selectedSortOption = 'area_asc';
        this.areaAutoSort = true;
      } else if (!hasArea && this.areaAutoSort) {
        this.selectedSortOption = '';
        this.areaAutoSort = false;
      }
    },

    applyPriceAutoSort(priceFrom, priceTo) {
      const hasPrice = (priceFrom !== '' && priceFrom !== null && priceFrom !== undefined) ||
                       (priceTo !== '' && priceTo !== null && priceTo !== undefined);
      if (hasPrice && !this.selectedSortOption) {
        this.selectedSortOption = 'price_asc';
        this.priceAutoSort = true;
      } else if (!hasPrice && this.priceAutoSort) {
        this.selectedSortOption = '';
        this.priceAutoSort = false;
      }
    },

    clearFilters() {
      this.priceFrom = "";
      this.priceTo = "";
      this.priceFromDisplay = "";
      this.priceToDisplay = "";
      this.areaFrom = "";
      this.areaTo = "";
      this.checkedTypes = [];
      this.selectedRooms = [];
      this.selectedFloors = [];
      this.selectedAdditional = [];
      this.selectedSortOption = "";
      this.areaAutoSort = false;
      this.priceAutoSort = false;
      sessionStorage.removeItem('flatFiltersState');
      sessionStorage.removeItem('flatTypePrefilter');
      this.applyFilters();
      this.closeAllDropdowns();
    },
    filterdTable() {
      this.applyFilters(false);
    },
    showModal(flatName) {
      this.flat = flatName;
      this.$modal.show('contact-form', { flat: flatName });
      document.body.style.overflow = 'hidden';
    },
    getTypesOf(flats) {
  flats.forEach((item) => {
    if (!this.typeOf.includes(item.type)) {
      this.typeOf.push(item.type)
    }
  })
  this.typeOf.sort((a, b) => a.localeCompare(b));
  // Przesuń "Mieszkanie" na początek, jeśli istnieje
  const idx = this.typeOf.indexOf("Mieszkanie");
  if (idx > 0) {
    this.typeOf.splice(idx, 1);
    this.typeOf.unshift("Mieszkanie");
  }
  this.enforceSingleTypeSelection();
  return this.typeOf
},

    enforceSingleTypeSelection() {
      if (this.typeOf.length === 1) {
        const onlyType = this.typeOf[0];
        if (this.checkedTypes.length !== 1 || this.checkedTypes[0] !== onlyType) {
          this.checkedTypes = [onlyType];
        }
      }
    },

    getPriceRange(flats) {
      const prices = [];

      flats.forEach((item) => {
        let priceToUse = item.price;

        if (item.promotion == 1 && item.promotion_price && Number(item.promotion_price) > 0 && 
            item.promotion_price_mkw && Number(item.promotion_price_mkw) > 0) {
          priceToUse = item.promotion_price;
        }

        const value = parseInt(priceToUse);
        if (!isNaN(value) && value > 0) {
          prices.push(value);
        }
      });

      const cenyBezZera = prices.filter(v => v > 0);
      if (cenyBezZera.length > 0) {
        const min = Math.min(...cenyBezZera);
        const max = Math.max(...cenyBezZera);
        this.minPrice = Math.floor(min / 10000) * 10000;
        this.maxPrice = Math.ceil(max / 10000) * 10000;
      } else {
        this.minPrice = 0;
        this.maxPrice = 10000000;
      }
    },
    getRoomsRange(flats) {      const roomsValues = [];

      flats.forEach((item) => {
        const value = parseInt(item.rooms);

        if (!isNaN(value) && value > 0) {
          roomsValues.push(value);
        }
      });

      if (roomsValues.length > 0) {
        this.minRooms = Math.min(...roomsValues);
        this.maxRooms = Math.max(...roomsValues);
      } else {
        this.minRooms = 1;
        this.maxRooms = 6;
      }

      this.roomOptions = [];
      for (let room = this.minRooms; room <= this.maxRooms; room += 1) {
        this.roomOptions.push(room);
      }
    },
    getAreaRange(flats) {
      const areas = [];

      flats.forEach((item) => {
        const value = parseInt((item.area && Number(item.area) > 0) ? item.area : item.area_usable);

        if (!isNaN(value) && value > 0) {
          areas.push(value);
        }
      });

      if (areas.length > 0) {
        const min = Math.min(...areas);
        const max = Math.max(...areas);
        this.minArea = Math.floor(min / 10) * 10;
        this.maxArea = Math.ceil(max / 10) * 10;
      } else {
        this.minArea = 0;
        this.maxArea = 500;
      }
    },

    getSelectedFloorLabel() {
      if (!this.selectedFloors.length) {
        return 'Wybierz';
      }
      if (this.selectedFloors.length === 1) {
        const f = this.selectedFloors[0];
        return f === 0 ? 'Parter' : 'Piętro ' + f;
      }
      return `${this.selectedFloors.length} wybrane`;
    },

    // Zapis stanu filtrów do sessionStorage
    saveFiltersState() {
      const state = {
        priceFrom: this.priceFrom,
        priceTo: this.priceTo,
        areaFrom: this.areaFrom,
        areaTo: this.areaTo,
        selectedRooms: this.selectedRooms,
        selectedFloors: this.selectedFloors,
        selectedAdditional: this.selectedAdditional,
        selectedSortOption: this.selectedSortOption,
        expandedRows: this.expandedRows
      };
      sessionStorage.setItem('flatFiltersState', JSON.stringify(state));
    },
    
    // Przywrócenie stanu filtrów z sessionStorage
    restoreFiltersState() {
      const saved = sessionStorage.getItem('flatFiltersState');
      if (saved) {
        try {
          const state = JSON.parse(saved);

          this.priceFrom = state.priceFrom ?? this.priceFrom;
          this.priceTo = state.priceTo ?? this.priceTo;
          this.areaFrom = state.areaFrom ?? this.areaFrom;
          this.areaTo = state.areaTo ?? this.areaTo;
          this.checkedTypes = [];
          this.selectedRooms = state.selectedRooms || this.selectedRooms;
          this.selectedFloors = state.selectedFloors || this.selectedFloors;
          this.selectedAdditional = state.selectedAdditional || this.selectedAdditional;
          this.selectedSortOption = state.selectedSortOption || this.selectedSortOption;
          this.expandedRows = state.expandedRows || this.expandedRows;
          this.enforceSingleTypeSelection();
          this.$nextTick(() => this.applyFilters());
          return true;
        } catch (e) {
          console.error('Błąd przywracania filtrów:', e);
        }
      }
      return false;
    },

    applyOneTimeTypePrefilter() {
      const raw = sessionStorage.getItem('flatTypePrefilter');
      if (!raw) {
        return false;
      }

      try {
        const payload = JSON.parse(raw);
        const selectedType = String(payload?.type || '').trim();
        if (!selectedType || !this.typeOf.includes(selectedType)) {
          sessionStorage.removeItem('flatTypePrefilter');
          return false;
        }

        this.priceFrom = '';
        this.priceTo = '';
        this.priceFromDisplay = '';
        this.priceToDisplay = '';
        this.areaFrom = '';
        this.areaTo = '';
        this.selectedRooms = [];
        this.selectedAdditional = [];
        this.selectedSortOption = '';
        this.areaAutoSort = false;
        this.priceAutoSort = false;
        this.checkedTypes = [selectedType];
        sessionStorage.removeItem('flatTypePrefilter');
        return true;
      } catch (error) {
        console.error('Błąd odczytu filtra typu lokalu:', error);
        sessionStorage.removeItem('flatTypePrefilter');
        return false;
      }
    },
    
    // NavigacjaFlata - zapisuje filtry i przechodzi na stronę mieszkania
    navigateToFlat(localNumber) {
      this.saveFiltersState();
      window.location.href = '/mieszkanie/' + encodeURIComponent(localNumber);
    },

    getFlatUrl(localNumber) {
      return '/mieszkanie/' + encodeURIComponent(localNumber);
    },

    hasAdditionalAreas(item) {
      return (
      (item.balkon && String(item.balkon).trim() !== "" && item.balkon !== "0") ||
      (item.taras && String(item.taras).trim() !== "" && item.taras !== "0") ||
      (item.loggia && String(item.loggia).trim() !== "" && item.loggia !== "0") ||
      (item.ogrod && String(item.ogrod).trim() !== "" && item.ogrod !== "0") ||
      (item.contiguity_list?.contiguity?.length > 0)
      );
    },

    getProspectLink(stage) {
      if (!this.pluginSettings) return null;
      let key;
      if (!stage || !String(stage).trim()) {
        key = 'prospect_brak-etapu';
      } else {
        key = 'prospect_' + this.sanitizeTitle(stage);
      }
      return this.pluginSettings[key] || null;
    },
    sanitizeTitle(str) {
      return String(str)
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9\-_]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
    },
    updateVisibleStages() {
      const stages = [...new Set(this.tableFlats.map(item => item.stage).filter(Boolean))];
      localStorage.setItem('visible_stages', JSON.stringify(stages));
    },

    goToPage(page) {
      const target = Number(page);
      if (!Number.isFinite(target)) {
        return;
      }
      this.currentPage = Math.min(Math.max(1, target), this.totalPages);
    },

    sanitizePhone(value) {
      return String(value || '').replace(/\s+/g, '');
    },

    formatAdvisorPhone(value) {
      const raw = String(value || '').trim();
      if (!raw) return '';

      const digits = raw.replace(/\D+/g, '');
      if (!digits) return raw;

      if (digits.length === 9) {
        return digits.match(/.{1,3}/g).join(' ');
      }

      if (digits.length > 9) {
        const local = digits.slice(-9);
        const country = digits.slice(0, -9);
        if (country) {
          return `+${country} ${local.match(/.{1,3}/g).join(' ')}`;
        }
      }

      return raw;
    },

    normalizeStickyOffset(value) {
      const parsed = Number(String(value ?? '').replace(',', '.'));
      if (!Number.isFinite(parsed) || parsed < 0) {
        return '20px';
      }

      return `${Math.round(parsed)}px`;
    },

    getDesktopGridColumns(viewportWidth = this.viewportWidth) {
      if (viewportWidth >= 2200) {
        return 5;
      }
      if (viewportWidth > 1720) {
        return 4;
      }
      if (viewportWidth > 1260) {
        return 3;
      }
      return 2;
    },

    getDefaultItemsPerPage(viewportWidth = this.viewportWidth) {
      return viewportWidth < 768
        ? 6
        : this.getDesktopGridColumns(viewportWidth) * 2;
    },

    updateItemsPerPageForViewport() {
      if (typeof window === 'undefined') {
        return;
      }

      this.viewportWidth = window.innerWidth;
      const autoItemsPerPage = this.getDefaultItemsPerPage(this.viewportWidth);
      const nextItemsPerPage = this.itemsPerPageMode === 'manual'
        ? Number(this.manualItemsPerPage || autoItemsPerPage)
        : autoItemsPerPage;

      if (nextItemsPerPage !== this.itemsPerPage) {
        this.itemsPerPage = nextItemsPerPage;
        this.currentPage = 1;
      }
    },

    resolveListingAdvisor() {
      const advisors = Array.isArray(this.pluginSettings?.advisors) ? this.pluginSettings.advisors : [];
      if (!advisors.length) {
        const fallback = {
          photo: this.pluginSettings?.advisor_photo || '',
          name: this.pluginSettings?.advisor_name || '',
          position: this.pluginSettings?.advisor_position || '',
          phone: this.pluginSettings?.advisor_phone || ''
        };

        if (fallback.photo || fallback.name || fallback.position || fallback.phone) {
          this.listingAdvisor = fallback;
          return;
        }

        this.listingAdvisor = null;
        return;
      }

      const rotationMinutes = Math.max(1, Number(this.pluginSettings?.advisor_rotation_minutes || 5));
      const bucket = Math.floor(Date.now() / (rotationMinutes * 60 * 1000));
      const advisorIndex = bucket % advisors.length;
      this.listingAdvisor = advisors[advisorIndex] || advisors[0] || null;
    },

    validateEmail() {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (this.listingContactForm.contact_email && !emailPattern.test(this.listingContactForm.contact_email)) {
        this.listingEmailError = 'Podaj prawidłowy adres email.';
        return false;
      }
      this.listingEmailError = '';
      return true;
    },

    validatePhone() {
      if (this.listingContactForm.contact_phone && this.listingContactForm.contact_phone.trim() !== '') {
        const phoneDigits = this.listingContactForm.contact_phone.replace(/\D/g, '');
        if (phoneDigits.length < 9) {
          this.listingPhoneError = 'Numer telefonu musi zawierać minimum 9 cyfr.';
          return false;
        }
      }
      this.listingPhoneError = '';
      return true;
    },

    submitListingContactForm() {
      // Walidacja pól
      const isEmailValid = this.validateEmail();
      const isPhoneValid = this.validatePhone();

      if (!isEmailValid || !isPhoneValid) {
        this.listingFormError = 'Popraw błędy w formularzu.';
        this.listingFormSuccess = false;
        return;
      }

      if (!this.listingConsent1 || !this.listingConsent2 || !this.listingConsent3) {
        this.listingFormError = 'Wymagane jest zaznaczenie wszystkich zgód.';
        this.listingFormSuccess = false;
        return;
      }

      this.listingFormSending = true;
      this.listingFormError = '';
      this.listingFormSuccess = false;

      const payload = {
        ...this.listingContactForm,
        contact_acceptance1: this.listingConsent1 ? '1' : '',
        contact_acceptance2: this.listingConsent2 ? '1' : '',
        contact_acceptance3: this.listingConsent3 ? '1' : ''
      };

      axios
        .post(this.url + '/wp-json/twoqbs/v1/send', payload)
        .then((response) => {
          const status = Number(response?.data?.status || 0);
          if (status === 200) {
            this.listingFormSuccess = true;
            this.listingFormError = '';
            this.listingContactForm.contact_name = '';
            this.listingContactForm.contact_email = '';
            this.listingContactForm.contact_phone = '';
            this.listingContactForm.contact_message = 'Proszę o kontakt w sprawie oferty.';
            this.listingConsent1 = false;
            this.listingConsent2 = false;
            this.listingConsent3 = false;
            return;
          }

          this.listingFormSuccess = false;
          this.listingFormError = 'Wystąpił błąd. Spróbuj ponownie.';
        })
        .catch(() => {
          this.listingFormSuccess = false;
          this.listingFormError = 'Wystąpił błąd. Spróbuj ponownie.';
        })
        .finally(() => {
          this.listingFormSending = false;
        });
    },

    setSliderBackground() {
    },

    compareByPriceMkw(a, b, direction = 'asc') {
      const priceA = Number(this.getDisplayPriceMkw(a));
      const priceB = Number(this.getDisplayPriceMkw(b));
      const hasPriceA = Number.isFinite(priceA) && priceA > 0;
      const hasPriceB = Number.isFinite(priceB) && priceB > 0;

      // Brak ceny za m2 zawsze na koncu listy.
      if (!hasPriceA && hasPriceB) return 1;
      if (hasPriceA && !hasPriceB) return -1;
      if (!hasPriceA && !hasPriceB) return 0;

      return direction === 'desc' ? priceB - priceA : priceA - priceB;
    }

  },

  computed: {
    sortedFlats() {
      const sorted = [...this.tableFlats];
      if (this.selectedSortOption === 'promotion') {
        sorted.sort((a, b) => Number(this.isPromotionActive(b)) - Number(this.isPromotionActive(a)));
      } else if (this.selectedSortOption === 'price_asc') {
        sorted.sort((a, b) => Number(this.isPromotionActive(a) ? a.promotion_price : a.price) - Number(this.isPromotionActive(b) ? b.promotion_price : b.price));
      } else if (this.selectedSortOption === 'price_desc') {
        sorted.sort((a, b) => Number(this.isPromotionActive(b) ? b.promotion_price : b.price) - Number(this.isPromotionActive(a) ? a.promotion_price : a.price));
      } else if (this.selectedSortOption === 'price_mkw_asc') {
        sorted.sort((a, b) => this.compareByPriceMkw(a, b, 'asc'));
      } else if (this.selectedSortOption === 'price_mkw_desc') {
        sorted.sort((a, b) => this.compareByPriceMkw(a, b, 'desc'));
      } else if (this.selectedSortOption === 'area_asc') {
        sorted.sort((a, b) => this.compareByArea(a, b, 'asc'));
      } else if (this.selectedSortOption === 'area_desc') {
        sorted.sort((a, b) => this.compareByArea(a, b, 'desc'));
      } else if (this.selectedSortOption === 'floor_asc') {
        sorted.sort((a, b) => {
          const fa = a.floor !== null && a.floor !== undefined && a.floor !== '' ? Number(a.floor) : Infinity;
          const fb = b.floor !== null && b.floor !== undefined && b.floor !== '' ? Number(b.floor) : Infinity;
          return fa - fb;
        });
      } else if (this.selectedSortOption === 'floor_desc') {
        sorted.sort((a, b) => {
          const fa = a.floor !== null && a.floor !== undefined && a.floor !== '' ? Number(a.floor) : -Infinity;
          const fb = b.floor !== null && b.floor !== undefined && b.floor !== '' ? Number(b.floor) : -Infinity;
          return fb - fa;
        });
      }

      return sorted;
    },

    totalPages() {
      const pages = Math.ceil(this.sortedFlats.length / this.itemsPerPage);
      return pages > 0 ? pages : 1;
    },

    paginatedFlats() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.sortedFlats.slice(start, end);
    },

    pageNumbers() {
      if (this.totalPages <= 4) {
        return Array.from({ length: this.totalPages }, (_, index) => index + 1);
      }

      const windowStart = Math.max(1, Math.min(this.currentPage - 1, this.totalPages - 2));
      const windowPages = [windowStart, windowStart + 1, windowStart + 2];

      if (windowPages[windowPages.length - 1] < this.totalPages - 1) {
        return [...windowPages, '...', this.totalPages];
      }

      return windowPages;
    },

    listingAdvisorPhone() {
      return this.formatAdvisorPhone(this.listingAdvisor?.phone || '');
    },

    investmentLogoUrl() {
      const candidates = [
        this.pluginSettings?.logo_url,
        this.investmentInfo?.logo_url,
        this.investmentInfo?.logo,
        this.investmentInfo?.logotype_url,
        this.investmentInfo?.logotype,
        this.investmentInfo?.image_logo
      ];

      const found = candidates.find((value) => typeof value === 'string' && value.trim() !== '');
      return found ? found.trim() : '';
    },

    listingConsentText1() {
      return this.pluginSettings?.consent_text_1 || this.listingConsentText1Default;
    },

    listingConsentParts() {
      const marker = '[[more]]';
      const text = String(this.listingConsentText1 || '');
      const markerIndex = text.indexOf(marker);

      if (markerIndex === -1) {
        return {
          hasMore: false,
          preview: text,
          more: ''
        };
      }

      return {
        hasMore: true,
        preview: text.slice(0, markerIndex).trim(),
        more: text.slice(markerIndex + marker.length).trim()
      };
    },

    hasListingConsentReadMore() {
      return this.listingConsentParts.hasMore;
    },

    listingConsentText1PreviewHtml() {
      return this.listingConsentParts.preview;
    },

    listingConsentText1MoreHtml() {
      return this.listingConsentParts.more;
    },

    listingConsentLegalIntro() {
      return this.pluginSettings?.consent_text_legal_intro || this.listingConsentLegalIntroDefault;
    },

    listingConsentText2() {
      return this.pluginSettings?.consent_text_2 || this.listingConsentText2Default;
    },

    listingConsentText3() {
      return this.pluginSettings?.consent_text_3 || this.listingConsentText3Default;
    },

    hasSingleType() {
      return this.typeOf.length === 1;
    },

      floorOptions() {
        if (!this.flats?.realestate) return [];

        const minPriceInput = this.parseFilterNumber(this.priceFrom);
        const maxPriceInput = this.parseFilterNumber(this.priceTo);
        const minAreaInput = this.parseFilterNumber(this.areaFrom);
        const maxAreaInput = this.parseFilterNumber(this.areaTo);

        const floorsSet = new Set();

        this.flats.realestate.forEach((item) => {
          if (item.dont_send_to_www == 0 && !this.isSoldStatus(item)) {
            const itemPrice = Number(this.isPromotionActive(item) ? item.promotion_price : item.price);
            const itemArea = this.getItemArea(item);
            const itemRooms = Number(item.rooms || 0);

            const inStage = this.checkedTypes.length !== 0 ? this.checkedTypes.includes(item.type) : true;
            const inPriceFrom = minPriceInput === null ? true : itemPrice >= minPriceInput;
            const inPriceTo = maxPriceInput === null ? true : itemPrice <= maxPriceInput;
            const inAreaFrom = minAreaInput === null ? true : itemArea >= minAreaInput;
            const inAreaTo = maxAreaInput === null ? true : itemArea <= maxAreaInput;
            const inRooms = this.selectedRooms.length ? this.selectedRooms.includes(itemRooms) : true;
            const inAdditional = this.selectedAdditional.length
              ? this.selectedAdditional.every((feature) => this.itemHasAdditionalFeature(item, feature))
              : true;
            const hasMainPrice = this.getDisplayPrice(item) !== null;

            if (inStage && inPriceFrom && inPriceTo && inAreaFrom && inAreaTo && inRooms && inAdditional && hasMainPrice) {
              const value = item.floor;
              if (value !== null && value !== undefined && value !== '') {
                const num = Number(value);
                if (Number.isFinite(num)) {
                  floorsSet.add(num);
                }
              }
            }
          }
        });

        return Array.from(floorsSet).sort((a, b) => a - b);
      },

    hasResidentialFlats() {
      if (!this.flats?.realestate) return false;
      return this.flats.realestate.some((item) => {
        const type = String(item?.type || '').trim().toLowerCase();
        return type === 'mieszkanie' || type === 'dom';
      });
    },

    availableSortOptions() {
      if (this.hasResidentialFlats) {
        return this.sortOptions;
      }
      return this.sortOptions.filter(o => o.value !== 'floor_asc' && o.value !== 'floor_desc');
    }





  },

  watch: {
    showMobileFilters(val) {
      const html = document.documentElement;
      if (val) {
        document.body.classList.add('no-scroll');
        document.body.style.overflow = 'hidden';
        document.body.style.touchAction = 'none';
        html.style.overflow = 'hidden';
      } else {
        document.body.classList.remove('no-scroll');
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
        html.style.overflow = '';
      }
    },
    priceFrom(val) {
      this.applyPriceAutoSort(val, this.priceTo);
      this.scheduleDynamicSearch();
    },
    priceTo(val) {
      this.applyPriceAutoSort(this.priceFrom, val);
      this.scheduleDynamicSearch();
    },
    areaFrom(val) {
      this.applyAreaAutoSort(val, this.areaTo);
      this.scheduleDynamicSearch();
    },
    areaTo(val) {
      this.applyAreaAutoSort(this.areaFrom, val);
      this.scheduleDynamicSearch();
    },
    selectedSortOption(val) {
      // Jeśli użytkownik zmienił sortowanie ręcznie (nie przez autoSort), wyłącz flagi
      if (val !== 'area_asc' || !this.areaAutoSort) {
        this.areaAutoSort = false;
      }
      if (val !== 'price_asc' || !this.priceAutoSort) {
        this.priceAutoSort = false;
      }
      this.scheduleDynamicSearch();
    },
    checkedTypes: {
      deep: true,
      handler() {
        this.scheduleDynamicSearch();
      }
    },
    selectedRooms: {
      deep: true,
      handler() {
        this.scheduleDynamicSearch();
      }
    },
    selectedFloors: {
      deep: true,
      handler() {
        this.scheduleDynamicSearch();
      }
    },
    selectedAdditional: {
      deep: true,
      handler() {
        this.scheduleDynamicSearch();
      }
    }
  },


  mounted() {
    this.outsideClickHandler = (event) => this.handleOutsideClick(event);
    document.addEventListener('click', this.outsideClickHandler);
    this.resizeHandler = () => this.updateItemsPerPageForViewport();
    window.addEventListener('resize', this.resizeHandler);
    this.loadPerPagePreference();
    this.updateItemsPerPageForViewport();

    // const autoUrl = window.location.origin + "/wp-json/twoqbs/v1/flats?nocache=" + new Date().getTime()
    // const autoUrlSettings = window.location.origin + "/wp-json/twoqbs/v1/settings?nocache=" + new Date().getTime()
    // const autoUrlInvestment = window.location.origin + "/wp-json/twoqbs/v1/investment?nocache=" + new Date().getTime()

    // const autoUrl = "https://springview.pl/wp-json/twoqbs/v1/flats?nocache=" + new Date().getTime()  //-> do testów lokalnych
    // const autoUrlSettings = "https://springview.pl/wp-json/twoqbs/v1/settings?nocache=" + new Date().getTime() // -> do testów lokalnych
    // const autoUrlInvestment = "https://springview.pl/wp-json/twoqbs/v1/investment?nocache=" + new Date().getTime() // -> do testów lokalnych

    const autoUrl = "https://test.2qbs.pl/wp-json/twoqbs/v1/flats?nocache=" + new Date().getTime()  //-> do testów lokalnych
    const autoUrlSettings = "https://test.2qbs.pl/wp-json/twoqbs/v1/settings?nocache=" + new Date().getTime() // -> do testów lokalnych
    const autoUrlInvestment = "https://test.2qbs.pl/wp-json/twoqbs/v1/investment?nocache=" + new Date().getTime() // -> do testów lokalnych
    axios.get(autoUrl).then((response) => {
      this.flats = response.data;
      this.tableFlats = Array.isArray(this.flats.realestate) ? this.flats.realestate : [];
      // console.log(this.tableFlats)
      this.tableFlats.sort(this.compare);
      this.getTypesOf(this.tableFlats)
      this.getPriceRange(this.tableFlats)
      this.getRoomsRange(this.tableFlats)
      this.getAreaRange(this.tableFlats)
      const hasTypePrefilter = this.applyOneTimeTypePrefilter();
      const restored = hasTypePrefilter ? false : this.restoreFiltersState();
      if (!restored) {
        this.applyFilters();
      }
    }).catch((error) => {
      console.error('Błąd ładowania lokali:', error);
      this.tableFlats = [];
      this.applyFilters();
    }).finally(() => {
      this.isFlatsLoading = false;
    });

    axios.get(autoUrlSettings).then((response) => {
      this.pluginSettings = response.data;
      this.mainColor = this.pluginSettings.main_color || '#63b0bd';
      this.secondaryColor = this.pluginSettings.secondary_color || '#2c3e50';
      this.backgroundColor = this.pluginSettings.background_color || '#f5f5f5';
      this.borderRadius = this.pluginSettings.border_radius || '16px';
      this.contactFormStickyOffset = this.normalizeStickyOffset(this.pluginSettings.contact_form_sticky_offset);
      this.resolveListingAdvisor();
      // console.log(this.pluginSettings)
    }).catch((error) => {
      console.error('Błąd ładowania ustawień wtyczki:', error);
    }).finally(() => {
      this.isSettingsLoading = false;
    });

    axios.get(autoUrlInvestment).then((response) => {
      this.investmentInfo = response.data;
      // console.log(this.investment)
    });

    axios.post(this.restUrl, this.contactForm).then((response) => {
      console.log(response);

    });

      this.$modal.$on('hidden', () => {
    document.body.style.overflow = '';
  });


  if (this.$modal && this.$modal.$on) {
    this.$modal.$on('opened', (modalName) => {
      if (modalName === 'contact-form') {
        document.body.style.overflow = 'hidden';
      }
    });
    this.$modal.$on('closed', (modalName) => {
      if (modalName === 'contact-form') {
        document.body.style.overflow = '';
      }
    });
  }

  // Fallback dla bfcache (iOS Safari itp.): mounted() nie odpala ponownie przy history.back()
  this.pageShowHandler = (event) => {
    if (event.persisted && sessionStorage.getItem('flatTypePrefilter')) {
      const applied = this.applyOneTimeTypePrefilter();
      if (applied) {
        this.$nextTick(() => this.applyFilters());
      }
    }
  };
  window.addEventListener('pageshow', this.pageShowHandler);

  },
  beforeDestroy() {
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    if (this.pageShowHandler) {
      window.removeEventListener('pageshow', this.pageShowHandler);
    }
    if (this.dynamicSearchTimer) {
      clearTimeout(this.dynamicSearchTimer);
    }
  }
};
</script>

<style >
#app {
  font-family: "Encode Sans", Sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
   margin-top: 60px;
  display: block;

}

#app.vue-plugin-wrapper .twoqbsAllFilters,
#app.vue-plugin-wrapper .twoqbsAllFilters * {
  box-sizing: border-box !important;
}

#app.vue-plugin-wrapper .twoqbsDesktopGutter {
  padding-left: 10px !important;
  padding-right: 10px !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

#app.vue-plugin-wrapper .twoqbsAllFilters.twoqbsDesktopGutter {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

#app.vue-plugin-wrapper .twoqbsFiltersDesktop {
  display: block !important;
  width: 100% !important;
}

#app.vue-plugin-wrapper .twoqbsFiltersColumns {
  display: grid !important;
  grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
  gap: 20px !important;
  width: 100% !important;
}

#app.vue-plugin-wrapper .twoqbsFilterColumn {
  display: grid !important;
  gap: 14px !important;
  align-content: start !important;
  min-width: 0 !important;
  grid-auto-rows: min-content !important;
}

#app.vue-plugin-wrapper .twoqbsFilterColumn > .twoqbsFilterBlock:first-child,
#app.vue-plugin-wrapper .twoqbsFilterColumn > .twoqbsFilterBlockWide:first-child {
  min-height: 90px !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: flex-start !important;
}

#app.vue-plugin-wrapper .twoqbsFilterBlock,
#app.vue-plugin-wrapper .twoqbsFilterBlockWide {
  min-width: 0 !important;
  width: 100% !important;
}

#app.vue-plugin-wrapper .twoqbsRangeInputs {
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  width: 100% !important;
  min-width: 0 !important;
}

#app.vue-plugin-wrapper .twoqbsRangeInputs input {
  min-width: 0 !important;
}

#app.vue-plugin-wrapper .twoqbsDropdownButton,
#app.vue-plugin-wrapper .twoqbsSearchButton,
#app.vue-plugin-wrapper .twoqbsClearButton,
#app.vue-plugin-wrapper .twoqbsRangeInputs input {
  max-width: 100% !important;
  font: inherit !important;
  line-height: 1.2 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

#app.vue-plugin-wrapper .twoqbsFilterLabel {
  margin: 0 0 8px 0 !important;
  line-height: 1.2 !important;
}


.simple-range-slider {
  bottom: -30px;
  background: none;
  box-shadow: none;
}

.modal {
  position: absolute;
  width: 500px;
  height: 500px;
  background-color: red;
}

.toggle-button {
  padding: 8px 12px;
  border: 1px solid #ccc;
  cursor: pointer;
  background: #f8f8f8;
  margin-bottom: 8px;
}

.twoqbsTypeCheckboxList {
  border: 1px solid #ddd;
  padding: 10px;
  max-height: 200px;
  overflow-y: auto;
  background: white;
}

.twoqbsFiltersDesktop {
  width: 100%;
}

.twoqbsFiltersColumns {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
}

.twoqbsFilterColumn {
  display: grid;
  gap: 14px;
  align-content: start;
}

.twoqbsFilterBlock {
  position: relative;
}

.twoqbsFilterBlockWide {
  min-width: 0;
  width: 100%;
}

.twoqbsFilterLabel {
  display: block;
  font-size: 15px;
  margin-bottom: 8px;
  font-weight: 600;
  text-align: left;
}

.twoqbsDropdownWrap {
  position: relative;
}

.twoqbsDropdownButton {
  width: 100%;
  text-align: left;
  border: 1px solid var(--main-color, #000000);
  background: #fff;
  padding: 10px 12px;
  height: 44px;
  min-height: 44px;
  box-sizing: border-box;
  color: #222;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.twoqbsDropdownButton.is-disabled {
  opacity: 1;
  cursor: default;
}

.twoqbsDropdownButton:disabled {
  opacity: 1;
}

.twoqbsDropdownArrow {
  width: 15px;
  height: 15px;
  border-right: 2px solid var(--main-color, #000000);
  border-bottom: 2px solid var(--main-color, #000000);
  transform: rotate(45deg);
  transition: transform 0.2s ease;
  margin-left: 10px;
  flex-shrink: 0;
}

.twoqbsDropdownArrow.is-open {
  transform: rotate(-135deg);
}

.twoqbsDropdownList {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  border: 1px solid #ddd;
  background: #fff;
  z-index: 20;
  padding: 8px 10px;
  max-height: 220px;
  overflow-y: auto;
}

.twoqbsPerPagePills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 44px;
  align-items: center;
}

.twoqbsPerPageBlock {
  min-height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.twoqbsPerPagePill {
  padding: 4px 10px;
  border: 1px solid #bfc6cc;
  border-radius: 20px;
  background: #fff;
  font-size: 13px;
  line-height: 1.4;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  white-space: nowrap;
}

.twoqbsPerPagePill:hover {
  border-color: var(--main-color, #000);
}

.twoqbsPerPagePill.is-active {
  background: var(--main-color, #000);
  border-color: var(--main-color, #000);
  color: #fff;
}

.twoqbsCheckboxRow {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.twoqbsCheckboxRow input[type="checkbox"],
.twoqbsTypeCheckboxList input[type="checkbox"] {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  min-height: 20px !important;
  -webkit-appearance: none !important;
  appearance: none !important;
  border: 1px solid #bfc6cc !important;
  border-radius: 4px !important;
  background: #ffffff !important;
  display: inline-grid;
  place-content: center;
  margin: 0;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  cursor: pointer;
}

.twoqbsCheckboxRow input[type="checkbox"]:checked,
.twoqbsTypeCheckboxList input[type="checkbox"]:checked {
  background: var(--main-color, #000000) !important;
  border-color: var(--main-color, #000000) !important;
}

.twoqbsCheckboxRow input[type="checkbox"]:checked::after,
.twoqbsTypeCheckboxList input[type="checkbox"]:checked::after {
  content: "";
  width: 10px;
  height: 6px;
  border-left: 2px solid #ffffff;
  border-bottom: 2px solid #ffffff;
  transform: rotate(-45deg) translateY(-1px);
}

.twoqbsCheckboxRow:last-child {
  margin-bottom: 0;
}

.twoqbsRangeInputs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
  min-width: 0;
}

.twoqbsRangeInputs input {
  width: 100%;
  min-width: 0;
  border: 1px solid var(--main-color, #000000);
  padding: 10px 12px;
  height: 44px;
  min-height: 44px;
  box-sizing: border-box;
}

.twoqbsFiltersDesktop .twoqbsRangeInputs input,
.mobile-filters-panel .twoqbsRangeInputs input {
  -webkit-appearance: none !important;
  appearance: none !important;
  background: #ffffff !important;
  border: 1px solid var(--main-color, #000000) !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  color: #222222 !important;
  font-size: 14px !important;
  height: 44px !important;
  min-height: 44px !important;
  box-sizing: border-box !important;
}

.twoqbsFiltersDesktop .twoqbsRangeInputs input:focus,
.mobile-filters-panel .twoqbsRangeInputs input:focus {
  outline: none !important;
  border-color: var(--main-color, #000000) !important;
  box-shadow: 0 0 0 1px var(--main-color, #000000) !important;
}

.twoqbsFiltersDesktop .twoqbsRangeInputs input::placeholder,
.mobile-filters-panel .twoqbsRangeInputs input::placeholder {
  color: #777777 !important;
  opacity: 1 !important;
}

.twoqbsFilterAction {
  align-self: end;
}

.twoqbsFilterActionRow {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.twoqbsPerPagePillsRow {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.twoqbsPerPagePillsRow .twoqbsFilterLabel {
  white-space: nowrap;
  margin-bottom: 0;
}

.twoqbsFilterActionColumn {
  margin-top: 0;
  display: flex;
  flex-direction: column;
  padding-top: 0;
  min-height: 90px;
  justify-content: flex-start;
}

.twoqbsFilterButtonsRow {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.twoqbsFilterLabelGhost {
  visibility: hidden;
}

#app.vue-plugin-wrapper .twoqbsFilterActionColumn {
  margin-top: 0 !important;
  padding-top: 0 !important;
  align-self: start !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: flex-start !important;
  min-height: 90px !important;
}

#app.vue-plugin-wrapper .twoqbsFilterActionColumn::before {
  display: none;
}

.twoqbsSearchButton,
.twoqbsClearButton {
  width: 100%;
  min-width: 0;
  min-height: 44px;
  border: 0;
  border-radius: var(--border-radius, 16px);
  background: var(--main-color, #000000);
  color: #fff;
  font-weight: 600;
  padding: 10px 20px;
}

.twoqbsClearButton {
  grid-column: 1;
}

.twoqbsSearchButton {
  grid-column: 2;
}

@media (max-width: 1200px) {
  .twoqbsFiltersColumns {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  #app.vue-plugin-wrapper .twoqbsFiltersColumns {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }
}

.btn-outline-dark {
  --bs-btn-hover-bg: var(--main-color, #000000) !important;
}

.twoqbsxml-initial-loader {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(255, 255, 255, 0.96);
}

.twoqbsxml-initial-loader__box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  min-width: 180px;
}

.twoqbsxml-initial-loader__spinner {
  width: 44px;
  height: 44px;
  border: 3px solid #e5e7eb;
  border-top-color: #222222;
  border-radius: 50%;
  animation: twoqbsxml-loader-spin 0.8s linear infinite;
}

.twoqbsxml-initial-loader__text {
  font-size: 15px;
  font-weight: 600;
  color: #222222;
  text-align: center;
}

@keyframes twoqbsxml-loader-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}


/* Styl panelu mobilnych filtrów */
.mobile-filters-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: flex-end;
  overscroll-behavior: contain;
  touch-action: none;
}

.mobile-filters-panel {
  background: #fff;
  width: 100%;
  border-radius: 16px 16px 0 0;
  padding: 24px 16px 20px;
  max-height: 95vh;
  overflow-y: auto;
  position: relative;
}

.mobile-filters-panel .mb-3 {
  margin-bottom: 16px !important;
}

.mobile-filters-panel .mb-3 b {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #222;
}

.mobile-filters-panel .twoqbsTypeCheckboxList {
  border: none;
  padding: 0;
  max-height: none;
  overflow: visible;
  background: transparent;
  white-space: normal !important;
}

.mobile-filters-panel .twoqbsTypeCheckboxList > div {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f1f1;
}

.mobile-filters-panel .twoqbsTypeCheckboxList > div:last-child {
  border-bottom: none;
}

.mobile-filters-panel .twoqbsTypeCheckboxList label {
  margin: 0;
  line-height: 1.35;
  text-align: left;
}

.mobile-filters-panel .twoqbsTypeCheckboxList input[type="checkbox"] {
  flex-shrink: 0;
}

.mobile-filters-panel .twoqbsRangeInputs {
  gap: 12px;
}

.mobile-filters-panel .twoqbsRangeInputs input {
  border-radius: var(--border-radius, 12px) !important;
  box-shadow: none !important;
}

.mobile-filters-panel .btn {
  min-height: 44px;
  border-radius: var(--border-radius, 16px);
  font-weight: 700;
}

.btn-close {
  position: absolute;
  top:  10px;
  right: 16px;
  background: none;
  border: none;
  font-size: 2rem;
  line-height: 1;
  color: #333;
}

@media (min-width: 768px) {
  .mobile-filters-overlay {
    display: none !important;
  }
}

@import "./assets/bootstrap.min.css";

.vm--modal input,
.vm--modal textarea {
  background-color: #ffffff;
  border-color: #e1e1e1;
  border-width: 0px 0px 1px 0px;
  border-radius: 0px 0px 0px 0px;
}

.vm--modal input,
.vm--modal textarea:focus {
  outline: none;
  border: 1px solid #e1e1e1;
  border-radius: 1px;
}

.vm--modal .form-check-label {
  font-size: 12px;
  text-align: left;
}

.vm--modal {
  height: auto !important;
}

.vm--container {
  overflow: auto;
}

.no-scroll {
  overflow: hidden !important;
  touch-action: none !important;
}

.twoqbsxml-layout {
  --twoqbs-card-max-width: 300px;
  --twoqbs-form-max-width: 400px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, var(--twoqbs-form-max-width));
  gap: 28px;
  align-items: start;
}

@media (max-width: 991px) {
  .twoqbsxml-layout {
    grid-template-columns: 1fr;
  }
}

/* ===== STYL KAFELKI - NOWY UKŁAD GRIDOWY ===== */

.twoqbsxml-grid {
  display: inline-grid;
  grid-template-columns: repeat(4, minmax(285px, 1fr));
  gap: 20px;
  padding: 0;
  margin-top: 10px;
  justify-items: end;
}

@media (min-width: 2200px) {
  .twoqbsxml-grid {
    grid-template-columns: repeat(5, minmax(285px, 1fr));
  }
}

@media (max-width: 1720px) {
  .twoqbsxml-grid {
    grid-template-columns: repeat(3, minmax(285px, 1fr));
  }
}

@media (max-width: 1260px) {
  .twoqbsxml-grid {
    grid-template-columns: repeat(2, minmax(285px, 1fr));
  }
}

@media (max-width: 768px) {
  .twoqbsxml-grid {
    display: grid !important;
    grid-template-columns: 1fr;
    gap: 20px;
    justify-items: center !important;
  }

  .twoqbsxml-card {
    min-width: 0;
    justify-self: center !important;
    margin-bottom: 20px !important;
  }
}

.twoqbsxml-card {
  box-sizing: border-box;
  position: relative;
  border: 1px solid var(--main-color, #000000);
  border-radius: var(--border-radius, 0px);
  overflow: hidden;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-width: 285px;
  max-width: var(--twoqbs-card-max-width, 300px);
  justify-self: end;
}

#app.vue-plugin-wrapper .twoqbsxml-card {
  border: 1px solid var(--main-color, #000000) !important;
}

.twoqbsxml-card-top {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  column-gap: 8px;
  min-height: 72px;
  padding: 14px 16px 10px;
  font-size: 14px;
  font-weight: 600;
}

.twoqbsxml-card-local {
  color: #222;
  justify-self: start;
  width: 100%;
  text-align: left;
  line-height: 20px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.twoqbsxml-card-status-text {
  color: var(--main-color, #000000);
  font-weight: 700;
  text-align: right;
  white-space: nowrap;
}

.twoqbsxml-status-promo {
  color: #dc3545;
}

.twoqbsxml-status-reserved {
  color: #808080;
}

.twoqbsxml-card-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px 8px;
  min-height: 190px;
}

#app.vue-plugin-wrapper .twoqbsxml-card-preview {
  min-height: 190px !important;
  max-height: 190px !important;
  overflow: hidden !important;
}

#app.vue-plugin-wrapper .twoqbsxml-card-preview > a {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  height: 190px !important;
  max-height: 190px !important;
  overflow: hidden !important;
}

.twoqbsxml-card-preview img {
  width: 100%;
  height: 190px;
  object-fit: contain;
}

#app.vue-plugin-wrapper .twoqbsxml-card-preview img {
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
  height: 190px !important;
  min-height: 190px !important;
  max-height: 190px !important;
  object-fit: contain !important;
  object-position: center center !important;
}

.twoqbsxml-card-preview-placeholder {
  width: 100%;
  height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  color: #777;
  font-size: 13px;
  border-radius: var(--border-radius, 16px);
}

.twoqbsxml-card-preview-logo {
  width: 100%;
  height: 250px;
  min-height: 250px;
  max-height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
}

.twoqbsxml-card-preview-logo img {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

#app.vue-plugin-wrapper .twoqbsxml-card-preview-placeholder {
  width: 100% !important;
  height: 250px !important;
  min-height: 250px !important;
  max-height: 250px !important;
}

.twoqbsxml-card-separator {
  height: 1px;
  background: #E3E3E3;
  margin: 16px 16px;
}

.twoqbsxml-card-stats {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px 16px;
  gap: 16px;
}

.twoqbsxml-card-stats-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  flex: 1;
}

.twoqbsxml-card-stats-label {
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
  color: #000000;
}

.twoqbsxml-card-stats-area {
  font-size: 22px;
  font-weight: 700;
  line-height: 28px;
  color: #000000;
}

.twoqbsxml-card-stats-area sup {
  font-size: 55%;
}

.twoqbsxml-card-stats-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.twoqbsxml-card-stats-rooms {
  font-size: 22px;
  font-weight: 700;
  line-height: 28px;
  text-align: center;
  color: #000000;
}

.twoqbsxml-card-stats-rooms-label {
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  line-height: 16px;
  color: #000000;
}

.twoqbsxml-card-extras {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  padding: 12px 16px;
}

.twoqbsxml-card-extra-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  line-height: 14px;
  color: #000000;
}

.twoqbsxml-extra-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  object-fit: contain;
}

.twoqbsxml-card-bottom {
  margin-top: auto;
  display: flex;
  flex-direction: column;
}

.twoqbsxml-card-price-main {
  padding: 12px 16px 0;
  font-size: 20px;
  font-weight: 700;
  color: #000000;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.twoqbsxml-price-info-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  z-index: 20;
}

.twoqbsxml-price-info-trigger {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid #666;
  background: #fff;
  color: #333;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;
}

.twoqbsxml-price-info-trigger:focus {
  outline: 2px solid var(--main-color, #000000);
  outline-offset: 1px;
}

.twoqbsxml-price-info-tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 220px;
  max-width: 280px;
  background: rgba(0, 0, 0, 0.9);
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  text-align: left;
  white-space: pre-line;
  border-radius: 8px;
  padding: 8px 10px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  z-index: 30;
}

.twoqbsxml-price-info-wrap:hover .twoqbsxml-price-info-tooltip,
.twoqbsxml-price-info-wrap:focus-within .twoqbsxml-price-info-tooltip {
  opacity: 1;
}

.twoqbsxml-card-price-old {
  padding: 4px 16px 0;
  font-size: 12px;
  color: #777;
  text-align: center;
  text-decoration: line-through;
}

.twoqbsxml-card-price-mkw {
  padding: 2px 16px 0;
  font-size: 13px;
  font-weight: 500;
  color: #000000;
  text-align: center;
}

.twoqbsxml-card-more {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 12px 16px 16px;
  padding: 10px;
  min-height: 52px;
  border: none;
  border-radius: var(--border-radius, 0px);
  background: var(--main-color, #000000);
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.3s;
}

.twoqbsxml-card-more:hover {
  opacity: 0.9;
  color: #ffffff;
}

.twoqbsxml-mobile-filters-btn {
  display: block;
  width: calc(100% - 32px);
  margin: 15px 16px 16px;
  padding: 12px 16px;
  box-sizing: border-box;
  border: none;
  border-radius: var(--border-radius, 16px);
  background: var(--main-color, #000000);
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  cursor: pointer;
  transition: opacity 0.3s;
}

.twoqbsxml-mobile-filters-btn:hover {
  opacity: 0.9;
  color: #ffffff;
}

.twoqbsxml-card-expansion {
  padding: 16px;
  border-top: 1px solid #f0f0f0;
  font-size: 13px;
  line-height: 1.6;
  color: #555;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.twoqbsxml-no-results {
  grid-column: 1 / -1;
  text-align: center;
  padding: 32px 20px;
  color: #dc3545;
  font-size: 16px;
  font-weight: 600;
}

.twoqbsxml-results-loader {
  grid-column: 1 / -1;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 20px;
  text-align: center;
}

.twoqbsxml-results-loader__spinner {
  width: 38px;
  height: 38px;
  border: 3px solid rgba(0, 0, 0, 0.12);
  border-top-color: var(--main-color, #000000);
  border-radius: 50%;
  animation: twoqbsxml-loader-spin 0.8s linear infinite;
}

.twoqbsxml-results-loader p {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #222222;
}

.twoqbsxml-pagination {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 18px;
}

.twoqbsxml-page-btn {
  border: 1px solid var(--main-color, #000000);
  background: #fff;
  color: #222;
  min-width: 40px;
  min-height: 38px;
  padding: 6px 12px;
}

.twoqbsxml-page-btn.is-active {
  background: var(--main-color, #000000);
  color: #fff;
}

.twoqbsxml-page-btn.is-ellipsis {
  border-color: transparent;
  background: transparent;
  color: #555;
  min-width: auto;
  padding: 6px 4px;
  cursor: default;
}

.twoqbsxml-page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.twoqbsxml-contact-sidebar {
  position: sticky;
  top: var(--contact-form-sticky-offset, 20px);
  margin-top: 10px;
  border: 1px solid var(--main-color, #000000);
  border-radius: var(--border-radius, 16px);
  padding: 18px;
  background: #fff;
  text-align: left;
  width: 100%;
  max-width: var(--twoqbs-form-max-width, 450px);
}

.twoqbsxml-contact-sidebar h3 {
  margin: 0 0 14px;
  font-size: 22px;
  font-weight: 700;
  text-align: center;
}

.twoqbsxml-advisor-info {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 14px;
}

.twoqbsxml-advisor-photo {
  width: 86px !important;
  height: 86px !important;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 86px;
}

.twoqbsxml-advisor-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  /* gap: 4px; */
}

.twoqbsxml-advisor-details h4 {
  margin: 0;
  font-size: 16px;
}

.twoqbsxml-advisor-details p {
  margin: 2px 0 0;
  font-size: 13px;
  color: #666;
}

.twoqbsxml-call-button {
  display: inline-block;
  background: var(--main-color, #000000);
  color: #fff;
  text-decoration: none;
  padding: 8px 14px;
  border-radius: var(--border-radius, 16px);
  margin-bottom: 14px;
}

.twoqbsxml-call-button:hover {
  color: #fff;
  opacity: 0.9;
}

.twoqbsxml-call-mobile {
  display: none;
}

.twoqbsxml-phone-desktop {
  display: inline-block;
  margin-top: 2px;
  color: var(--main-color, #000000);
  font-size: 18px;
  font-weight: 700;
  text-decoration: none;
}

.twoqbsxml-phone-desktop:hover {
  text-decoration: underline;
}

.twoqbsxml-contact-form {
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr);
  width: 100%;
  min-width: 0;
}

.twoqbsxml-contact-form input,
.twoqbsxml-contact-form textarea {
  width: 100%;
  border: 1px solid var(--main-color, #000000);
  padding: 10px 12px;
  border-radius: var(--border-radius, 16px);
}

#app.vue-plugin-wrapper .twoqbsxml-contact-form input,
#app.vue-plugin-wrapper .twoqbsxml-contact-form textarea {
  -webkit-appearance: none !important;
  appearance: none !important;
  background: #ffffff !important;
  border: 1px solid var(--main-color, #000000) !important;
  border-radius: var(--border-radius, 16px) !important;
  box-shadow: none !important;
  color: #222222 !important;
  font-size: 14px !important;
  line-height: 1.35 !important;
  padding: 10px 12px !important;
  margin: 0 !important;
  max-width: 100% !important;
  width: 100% !important;
  min-width: 0 !important;
  display: block !important;
  flex: 0 0 auto !important;
}

#app.vue-plugin-wrapper .twoqbsxml-contact-form > input[type="text"],
#app.vue-plugin-wrapper .twoqbsxml-contact-form > input[type="email"],
#app.vue-plugin-wrapper .twoqbsxml-contact-form > input[type="tel"],
#app.vue-plugin-wrapper .twoqbsxml-contact-form > textarea {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
}

#app.vue-plugin-wrapper .twoqbsxml-contact-form input:focus,
#app.vue-plugin-wrapper .twoqbsxml-contact-form textarea:focus {
  outline: none !important;
  border-color: var(--main-color, #000000) !important;
  box-shadow: 0 0 0 1px var(--main-color, #000000) !important;
}

#app.vue-plugin-wrapper .twoqbsxml-consent-row input[type="checkbox"] {
  width: 15px !important;
  height: 15px !important;
  min-width: 15px !important;
  min-height: 15px !important;
  max-width: 15px !important;
  max-height: 15px !important;
  flex: 0 0 15px !important;
  padding: 0 !important;
  box-sizing: border-box !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  border: 1px solid #bfc6cc !important;
  border-radius: 4px !important;
  background: #ffffff !important;
  display: inline-grid !important;
  place-content: center !important;
  margin: 0 !important;
  box-shadow: none !important;
  transform: none !important;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  cursor: pointer;
}

#app.vue-plugin-wrapper .twoqbsxml-consent-row input[type="checkbox"]:checked {
  background: var(--main-color, #000000) !important;
  border-color: var(--main-color, #000000) !important;
}

#app.vue-plugin-wrapper .twoqbsxml-consent-row input[type="checkbox"]:checked::after {
  content: "";
  width: 7px;
  height: 4px;
  border-left: 2px solid #ffffff;
  border-bottom: 2px solid #ffffff;
  transform: rotate(-45deg) translateY(-1px);
}

.twoqbsxml-contact-form textarea {
  min-height: 90px;
  resize: vertical;
}

.twoqbsxml-consent-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 10px;
  line-height: 1.4;
}

.twoqbsxml-consent-row input {
  width: auto;
  margin: 0;
}

.twoqbsxml-consent-text,
.twoqbsxml-consent-text span {
  display: inline;
}

.twoqbsxml-consent-title {
  display: block;
  font-weight: 500;
}

.twoqbsxml-read-more-link {
  color: var(--main-color, #000000);
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
  line-height: inherit;
  margin-left: 6px;
}

.twoqbsxml-consent-more-inline {
  margin-left: 6px;
}

.twoqbsxml-submit-button {
  border: 0;
  background: var(--main-color, #000000);
  color: #fff;
  font-weight: 600;
  min-height: 42px;
  border-radius: var(--border-radius, 16px);
}

.twoqbsxml-submit-button:disabled {
  opacity: 0.6;
}

.twoqbsxml-form-success,
.twoqbsxml-form-error {
  border-radius: var(--border-radius, 16px);
  padding: 10px 12px;
  font-size: 14px;
  margin-bottom: 12px;
}

@media (max-width: 991px) {
  .twoqbsxml-call-mobile {
    display: inline-block;
  }

  .twoqbsxml-phone-desktop {
    display: none;
  }
}

.twoqbsxml-form-success {
  background: #e6f7ec;
  color: #1f7a39;
}

.twoqbsxml-form-success-inline {
  margin-top: 8px;
  margin-bottom: 10px;
}

.twoqbsxml-form-error {
  background: #fdecec;
  color: #b3261e;
}

.twoqbsxml-input-error {
  border-color: #b3261e !important;
  background-color: #fff5f5 !important;
}

.twoqbsxml-field-error {
  color: #b3261e;
  font-size: 13px;
  margin-top: 4px;
  text-align: left;
  padding-left: 4px;
}

.twoqbsxml-contact-form > div {
  width: 100%;
}

</style>
